'use client';

import { useMergeRefs } from '@floating-ui/react';
import {
  type ComponentProps,
  type MouseEventHandler,
  forwardRef,
  useCallback,
  useContext,
  useMemo,
} from 'react';

import { Slot } from '../slot';
import { ModalRootContext } from './context/modal-root.context';
import { useModalRegistry } from './hooks/use-modal-registry';
import type { ModalTriggerPropsInterface } from './types/modal-trigger.types';

/**
 * @since 10.0.0
 */
export const ModalTrigger = forwardRef<HTMLButtonElement, ModalTriggerPropsInterface>(
  (props: ModalTriggerPropsInterface, forwardedRef) => {
    const { children, multiModalTriggerId, onClick, ...rest } = props;
    const { open, isActive } = useModalRegistry();
    const { modal } = useContext(ModalRootContext);

    // Modal could be undefined if using multi modal triggers
    const mergedRefs = useMergeRefs([modal?.refs.setReference, forwardedRef]);

    // Check if using multi modal trigger id and current one is active
    const isModalActive = multiModalTriggerId ? isActive(multiModalTriggerId) : false;

    // When multi modal trigger id is used, open modal via registry
    const onClickCallback = useCallback<MouseEventHandler<HTMLButtonElement>>(
      (event) => {
        open(multiModalTriggerId, event);
        onClick?.(event);
      },
      [multiModalTriggerId, open, onClick],
    );

    // When multi modal trigger id is used, manage a11y props ourselves
    const a11yModalProps: ComponentProps<'button'> = useMemo(
      () => ({
        'aria-expanded': isModalActive,
        'aria-haspopup': 'dialog',
        'aria-controls': isModalActive ? multiModalTriggerId : undefined,
        'onClick': onClickCallback,
      }),
      [isModalActive, multiModalTriggerId, onClickCallback],
    );

    return (
      <Slot
        data-c11n-version={__C11N_VERSION__}
        data-c11n-component="Modal.Trigger"
        // Conditionally swap ref, props, and handlers between modal registry and floating UI
        ref={multiModalTriggerId ? forwardedRef : mergedRefs}
        {...(multiModalTriggerId ? { ...a11yModalProps, ...rest } : modal.getReferenceProps(rest))}
      >
        {children}
      </Slot>
    );
  },
);

ModalTrigger.displayName = 'Modal.Trigger';
